import axiosInstance from "service/axiosInstance";
import {
  CONTACT_IMPORT_MODULE,
  CONTACT_IMPORT_PAGE_SIZE,
  ContactImportCreateRequest,
  ContactImportCreateResponse,
  ContactImportErrorCategory,
  ContactImportErrorPage,
  ContactImportErrorsResponse,
  ContactImportFormatLabel,
  ContactImportMappingRequest,
  ContactImportMappingResponse,
  ContactImportCompanyFallbackRequest,
  ContactImportCompanyFallbackResponse,
  ContactImportModule,
  ContactImportOwnerFallbackRequest,
  ContactImportOwnerFallbackResponse,
  ContactImportRecord,
  ContactImportValueMappingRequest,
  ContactImportValueMappingResponse,
  ContactImportUpload,
  ContactImportUploadUrlData,
  ContactImportUploadUrlRequest,
  ContactImportUploadUrlResponse,
} from "types/crm/contactImport";
import { getContactImportFormat } from "utils/crmContactImport";
import { BusinessError } from "utils/normalizeApiError";

/**
 * Getting the selected import file onto S3, then opening an import for it.
 *
 * The upload-url call only mints a presigned URL — the file itself goes
 * straight to S3, so it never passes through the backend.
 */

const UPLOAD_URL_PATH = "/crm/imports/upload-url";
const IMPORTS_PATH = "/crm/imports";
const TEMPLATE_PATH = "/crm/imports/template";

/**
 * Every call that answers with an import pages its big lists the same way.
 * A save asks for the first page; the Dropdowns step asks `getImport` for a
 * larger one when the user presses Show more.
 */
const IMPORT_PAGE_PARAMS = { page: 0, size: CONTACT_IMPORT_PAGE_SIZE };

const isFilledString = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0;

const isPositiveNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value) && value > 0;

/** What each supported format is uploaded as. */
const S3_CONTENT_TYPES: Record<ContactImportFormatLabel, string> = {
  CSV: "text/csv",
  XLSX: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
};

/**
 * The content type to upload under.
 *
 * The presigned URL signs `content-type`, so the header has to match what the
 * API signed. The API is given only the file name, so the type is taken from
 * the extension too, rather than from `file.type` — browsers report that
 * inconsistently for spreadsheets (a .csv often arrives as an Excel type).
 */
const getUploadContentType = (file: File): string => {
  const format = getContactImportFormat(file.name);
  return format ? S3_CONTENT_TYPES[format.label] : file.type || "application/octet-stream";
};

/** Dev-only, and never with the signed URL in it. */
const logImportStep = (message: string, payload: unknown): void => {
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.log(message, payload);
  }
};

/**
 * Asks the API where to put the file.
 *
 * @throws {BusinessError} when the response is missing anything the upload
 * needs, so a malformed payload fails here rather than halfway through S3.
 */
const createUploadUrl = async (
  payload: ContactImportUploadUrlRequest
): Promise<ContactImportUploadUrlData> => {
  const { data } = await axiosInstance.post<ContactImportUploadUrlResponse>(
    UPLOAD_URL_PATH,
    payload
  );

  if (!data?.success || !data.data) {
    throw new BusinessError(
      data?.message || "Could not prepare the file upload. Please try again."
    );
  }

  const upload = data.data;

  if (
    !isFilledString(upload.mediaUuid) ||
    !isFilledString(upload.uploadUrl) ||
    !isFilledString(upload.s3Key) ||
    !isPositiveNumber(upload.expiresInSeconds) ||
    !isPositiveNumber(upload.maxSizeBytes) ||
    !isPositiveNumber(upload.maxRows)
  ) {
    throw new BusinessError(
      "Could not prepare the file upload because the server response was incomplete."
    );
  }

  logImportStep(`[CRM Import] ${data.message}`, {
    ...data,
    // The signed URL is a credential, so it never reaches a log.
    data: { ...upload, uploadUrl: "[redacted presigned url]" },
  });

  return upload;
};

/**
 * Puts the file on S3 with the presigned URL.
 *
 * @throws {BusinessError} for anything the user can act on — an expired link, a
 * rejected upload, or a dropped connection.
 */
const uploadFileToS3 = async (uploadUrl: string, file: File): Promise<void> => {
  let response: Response;

  try {
    response = await fetch(uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": getUploadContentType(file) },
      // The raw file, so the bytes arrive exactly as they left the disk.
      body: file,
    });
  } catch {
    throw new BusinessError(
      "The file could not be uploaded. Check your connection and try again."
    );
  }

  // The link is signed and short-lived, so S3 turns it down once it lapses or
  // if the request does not match what was signed.
  if (response.status === 403) {
    throw new BusinessError(
      "The upload was refused — the link may have expired. Please try uploading the file again."
    );
  }

  if (!response.ok) {
    throw new BusinessError(
      `The file could not be uploaded (error ${response.status}). Please try again.`
    );
  }
};

/**
 * Selected file → presigned URL → S3 → what the next step needs.
 *
 * The returned handle is all the later steps use to refer to the file, so
 * nothing downstream has to hold the File object or the signed URL.
 */
const uploadImportFile = async (file: File): Promise<ContactImportUpload> => {
  const upload = await createUploadUrl({
    module: CONTACT_IMPORT_MODULE,
    originalFileName: file.name,
    fileSizeBytes: file.size,
  });
  await uploadFileToS3(upload.uploadUrl, file);

  logImportStep("[CRM Import] File uploaded to S3 successfully", {
    mediaUuid: upload.mediaUuid,
    s3Key: upload.s3Key,
    fileName: file.name,
    fileSizeBytes: file.size,
  });

  return {
    mediaUuid: upload.mediaUuid,
    s3Key: upload.s3Key,
    fileName: file.name,
    fileSizeBytes: file.size,
    maxSizeBytes: upload.maxSizeBytes,
    maxRows: upload.maxRows,
  };
};

/**
 * Opens an import for a file already on S3, named by its mediaUuid.
 *
 * @throws {BusinessError} when the import was not created, or came back
 * without the identity the later steps need.
 */
const createImport = async (
  payload: ContactImportCreateRequest
): Promise<ContactImportRecord> => {
  if (!isFilledString(payload.mediaUuid)) {
    throw new BusinessError("Upload the file again before starting the import.");
  }

  try {
    const { data } = await axiosInstance.post<ContactImportCreateResponse>(
      IMPORTS_PATH,
      payload,
      { params: IMPORT_PAGE_PARAMS }
    );

    if (!data?.success || !data.data) {
      throw new BusinessError(
        data?.message || "Could not start the import. Please try again."
      );
    }

    const record = data.data;

    if (!isFilledString(record.uuid)) {
      throw new BusinessError(
        "Could not start the import because the server response was incomplete (missing import uuid)."
      );
    }

    logImportStep(`[CRM Import] ${data.message}`, data);

    return record;
  } catch (error: any) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.error("[CRM Import] POST /crm/imports error:", {
        payload,
        status: error?.response?.status,
        message: error?.response?.data?.message || error?.message,
        backendErrors: error?.response?.data?.errors || error?.response?.data?.response?.errors,
        fullBackendResponse: error?.response?.data,
        error,
      });
    }
    throw error;
  }
};

/**
 * Fetches the blank import template for a module.
 *
 * The file is built from the API's own field catalog, so it always names the
 * columns the import expects — nothing is generated here.
 *
 * @throws {BusinessError} when the template comes back empty or unreadable.
 */
const downloadTemplate = async (
  module: ContactImportModule = CONTACT_IMPORT_MODULE
): Promise<Blob> => {
  const { data } = await axiosInstance.get<Blob>(TEMPLATE_PATH, {
    params: { module },
    responseType: "blob",
  });

  if (!(data instanceof Blob) || data.size === 0) {
    throw new BusinessError("The import template could not be downloaded. Please try again.");
  }

  logImportStep("[CRM Import] Import template downloaded successfully", {
    module,
    sizeBytes: data.size,
    type: data.type,
  });

  return data;
};

/**
 * Reads an import back — the columns, dropdown values and Owner / Company
 * matches as they stand right now.
 *
 * The large lists inside are paged independently, each alongside its own
 * `total*Count`, so a caller pages the one it is showing.
 */
const getImport = async (
  importUuid: string,
  params: { page?: number; size?: number } = IMPORT_PAGE_PARAMS
): Promise<ContactImportRecord> => {
  const { data } = await axiosInstance.get<ContactImportCreateResponse>(
    `${IMPORTS_PATH}/${importUuid}`,
    { params }
  );

  if (!data?.success || !data.data) {
    throw new BusinessError(
      data?.message || "Could not load the import. Please try again."
    );
  }

  logImportStep(`[CRM Import] ${data.message}`, data);

  return data.data;
};

/**
 * Reads a page of the import's error log.
 *
 * The job itself only carries the first hundred, which is a preview — a file
 * with a thousand bad rows says so in `totalErrorCount` and keeps the rest
 * here. Which log this is depends on where the job has got to: the dry run's
 * findings after validate, that run's own once it has been started.
 *
 * @throws {BusinessError} when the log could not be read.
 */
const getImportErrors = async (
  importUuid: string,
  params: {
    page?: number;
    size?: number;
    category?: ContactImportErrorCategory;
  } = {}
): Promise<ContactImportErrorPage> => {
  if (!isFilledString(importUuid)) {
    throw new BusinessError("Cannot read errors without a valid import session.");
  }

  const { data } = await axiosInstance.get<ContactImportErrorsResponse>(
    `${IMPORTS_PATH}/${importUuid}/errors`,
    {
      params: {
        page: params.page ?? 0,
        size: params.size ?? CONTACT_IMPORT_PAGE_SIZE,
        category: params.category || undefined,
      },
    }
  );

  if (!data?.success || !data.data) {
    throw new BusinessError(
      data?.message || "Could not load the import's errors. Please try again."
    );
  }

  logImportStep(`[CRM Import] ${data.message}`, data);

  const page = data.data;

  // A log with nothing in it comes back without the array rather than with an
  // empty one, so it is settled here and every caller can just read it.
  return {
    ...page,
    items: page.items ?? [],
    totalCount: page.totalCount ?? 0,
    totalPages: page.totalPages ?? 0,
  };
};

/**
 * Saves which field each uploaded column feeds.
 *
 * The call merges into the mapping already saved, so a column left out of the
 * body keeps the field it had. Sending the whole screen — including the columns
 * left out, as `null` — is what makes un-mapping one stick.
 *
 * @throws {BusinessError} when the mapping was refused — two columns on one
 * field, or an import that has already started.
 */
const updateMapping = async (
  importUuid: string,
  payload: ContactImportMappingRequest
): Promise<ContactImportRecord> => {
  if (!isFilledString(importUuid)) {
    throw new BusinessError("Upload the file again before saving the mapping.");
  }

  const { data } = await axiosInstance.put<ContactImportMappingResponse>(
    `${IMPORTS_PATH}/${importUuid}/mapping`,
    payload,
    { params: IMPORT_PAGE_PARAMS }
  );

  if (!data?.success || !data.data) {
    throw new BusinessError(
      data?.message || "Could not save the field mapping. Please try again."
    );
  }

  logImportStep(`[CRM Import] ${data.message}`, data);

  return data.data;
};

/**
 * Saves one screen's worth of value mappings.
 *
 * The body carries only the fields that screen asked about — the API leaves
 * every other field's mapping as it was.
 *
 * @throws {BusinessError} when the mapping was refused, e.g. an import that has
 * already started.
 */
const updateValueMapping = async (
  importUuid: string,
  payload: ContactImportValueMappingRequest
): Promise<ContactImportRecord> => {
  if (!isFilledString(importUuid)) {
    throw new BusinessError("Upload the file again before saving these values.");
  }

  const path = `${IMPORTS_PATH}/${importUuid}/value-mapping`;

  // The body exactly as it goes over the wire — the uuid is in the path, not
  // in it, so this is what the API is judged on.
  logImportStep(`[CRM Import] PUT ${path} — request body`, payload);

  const { data } = await axiosInstance.put<ContactImportValueMappingResponse>(
    path,
    payload,
    { params: IMPORT_PAGE_PARAMS }
  );

  // Logged before the envelope is judged, so a refusal is as visible as a save.
  logImportStep(`[CRM Import] PUT ${path} — response`, data);

  if (!data?.success || !data.data) {
    throw new BusinessError(
      data?.message || "Could not save these values. Please try again."
    );
  }

  return data.data;
};

/**
 * Answers the Owner decision for every row the import could not resolve —
 * blanks, unknown emails and names shared by more than one person.
 *
 * One call settles the whole set; the import comes back carrying the choice as
 * `ownerMapping.resolution`.
 *
 * @throws {BusinessError} when the decision was refused — an import that has
 * already started, or a module other than contacts.
 */
const setOwnerFallback = async (
  importUuid: string,
  payload: ContactImportOwnerFallbackRequest
): Promise<ContactImportRecord> => {
  if (!isFilledString(importUuid)) {
    throw new BusinessError("Upload the file again before choosing an owner.");
  }

  const { data } = await axiosInstance.post<ContactImportOwnerFallbackResponse>(
    `${IMPORTS_PATH}/${importUuid}/owner-fallback`,
    payload,
    { params: IMPORT_PAGE_PARAMS }
  );

  if (!data?.success || !data.data) {
    throw new BusinessError(
      data?.message || "Could not save the owner choice. Please try again."
    );
  }

  logImportStep(`[CRM Import] ${data.message}`, data);

  return data.data;
};

/**
 * Answers the Company decision for every contact the import could not resolve —
 * an unknown name or no name at all.
 *
 * `LINK_TO_EXISTING` points the whole set at one company that already exists;
 * an import never creates one.
 *
 * @throws {BusinessError} when the decision was refused — no company given for
 * a link, an unknown company, or an import that has already started.
 */
const setCompanyFallback = async (
  importUuid: string,
  payload: ContactImportCompanyFallbackRequest
): Promise<ContactImportRecord> => {
  if (!isFilledString(importUuid)) {
    throw new BusinessError("Upload the file again before choosing a company.");
  }

  if (payload.action === "LINK_TO_EXISTING" && !isFilledString(payload.companyUuid)) {
    throw new BusinessError("Choose the company these contacts should be linked to.");
  }

  const { data } = await axiosInstance.post<ContactImportCompanyFallbackResponse>(
    `${IMPORTS_PATH}/${importUuid}/company-fallback`,
    payload,
    { params: IMPORT_PAGE_PARAMS }
  );

  if (!data?.success || !data.data) {
    throw new BusinessError(
      data?.message || "Could not save the company choice. Please try again."
    );
  }

  logImportStep(`[CRM Import] ${data.message}`, data);

  return data.data;
};

/**
 * Validates the parsed rows against the CRM constraints (required fields, format checks).
 * Triggers a backend validation job.
 */
const validateImport = async (importUuid: string): Promise<ContactImportRecord> => {
  if (!isFilledString(importUuid)) {
    throw new BusinessError("Cannot validate without a valid import session.");
  }

  const { data } = await axiosInstance.post<ContactImportCreateResponse>(
    `${IMPORTS_PATH}/${importUuid}/validate`,
    undefined,
    { params: IMPORT_PAGE_PARAMS }
  );

  if (!data?.success || !data.data) {
    throw new BusinessError(
      data?.message || "Could not validate the import file. Please try again."
    );
  }

  logImportStep(`[CRM Import] Validation requested`, data);

  return data.data;
};

/**
 * Starts the contact import job once validation and review are approved.
 */
const startImport = async (importUuid: string): Promise<ContactImportRecord> => {
  if (!isFilledString(importUuid)) {
    throw new BusinessError("Cannot start without a valid import session.");
  }

  const { data } = await axiosInstance.post<ContactImportCreateResponse>(
    `${IMPORTS_PATH}/${importUuid}/start`
  );

  if (!data?.success || !data.data) {
    throw new BusinessError(
      data?.message || "Could not start the import. Please try again."
    );
  }

  logImportStep(`[CRM Import] ${data.message}`, data);

  return data.data;
};

export const contactImportQueryKeys = {
  all: ["contact-imports"] as const,
  /**
   * The import itself. Also the prefix every page of it shares, so a write
   * meant for "this import" reaches whichever page is cached.
   */
  detail: (importUuid: string) =>
    [...contactImportQueryKeys.all, "detail", importUuid] as const,
  /** One import read at a given page size. */
  detailPage: (importUuid: string, size: number) =>
    [...contactImportQueryKeys.detail(importUuid), size] as const,
  /** One page of one import's error log, under a given filter. */
  errors: (importUuid: string, page: number, size: number, category?: string) =>
    [
      ...contactImportQueryKeys.all,
      "errors",
      importUuid,
      page,
      size,
      category ?? "",
    ] as const,
};

export const contactImportApi = {
  downloadTemplate,
  createUploadUrl,
  getImport,
  getImportErrors,
  uploadFileToS3,
  uploadImportFile,
  createImport,
  updateMapping,
  updateValueMapping,
  setOwnerFallback,
  setCompanyFallback,
  validateImport,
  startImport,
};
